import React, { useEffect, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Trophy, TriangleAlert, ArrowUpRight, IndianRupee, FolderKanban, ShieldCheck, Users as UsersIcon } from "lucide-react";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function Kpi({ label, value, icon: Icon, tone = "stone" }) {
    return (
        <div data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`} className="bg-white border border-stone-200 rounded-xl p-5">
            <div className="flex items-start justify-between">
                <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
                <Icon size={16} className={tone === "amber" ? "text-amber-600" : "text-stone-400"} strokeWidth={1.5} />
            </div>
            <div className="mt-2 text-2xl font-heading font-semibold text-stone-900">{value}</div>
        </div>
    );
}

export default function Dashboard() {
    const { user, isAdmin, isApprover, hasPerm } = useAuth();
    const [data, setData] = useState(null);
    const [my, setMy] = useState(null);
    const [assignments, setAssignments] = useState(null);

    useEffect(() => {
        const load = async () => {
            if (isAdmin() || isApprover() || hasPerm("dashboard.master")) {
                try { const { data } = await api.get("/dashboard/master"); setData(data); } catch { /* */ }
                try { const { data: a } = await api.get("/dashboard/assignments"); setAssignments(a); } catch { /* */ }
            }
            try { const { data: m } = await api.get("/dashboard/my"); setMy(m); } catch { /* */ }
        };
        load();
    }, [user]); // eslint-disable-line

    const canSeeMaster = isAdmin() || isApprover();

    return (
        <div data-testid="dashboard-page" className="space-y-8">
            <div>
                <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Overview</div>
                <h1 className="text-3xl font-heading font-semibold text-stone-900 tracking-tight">
                    Welcome back, {user?.name?.split(" ")[0]}.
                </h1>
                <p className="text-sm text-stone-500 mt-1">
                    {canSeeMaster ? "Master view — projects, savings, mismatches and order status." : "Your assigned projects and quotations."}
                </p>
            </div>

            {canSeeMaster && data && (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Kpi label="Active projects" value={data.project_count} icon={FolderKanban} />
                        <Kpi label="Total potential saving" value={inr(data.grand_saving)} icon={IndianRupee} tone="amber" />
                        <Kpi label="Total budget" value={inr(data.grand_budget)} icon={ShieldCheck} />
                        <Kpi label="Total awarded" value={inr(data.grand_awarded)} icon={Trophy} tone="amber" />
                    </div>

                    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
                            <div>
                                <div className="font-heading font-semibold text-stone-900">Projects</div>
                                <div className="text-xs text-stone-500">Vendors quoted, cheapest, saving, mismatches, budget</div>
                            </div>
                            <Link data-testid="projects-link" to="/projects" className="text-sm text-amber-700 hover:text-amber-900 flex items-center gap-1">
                                View all <ArrowUpRight size={14} />
                            </Link>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                        <th className="text-left py-3 px-4">Project</th>
                                        <th className="text-right py-3 px-4">Vendors</th>
                                        <th className="text-left py-3 px-4">Cheapest</th>
                                        <th className="text-right py-3 px-4">Total</th>
                                        <th className="text-right py-3 px-4">Saving</th>
                                        <th className="text-right py-3 px-4">Mismatches</th>
                                        <th className="text-left py-3 px-4">Budget</th>
                                        <th className="text-left py-3 px-4">Order</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.projects || []).map((p) => (
                                        <tr key={p.project_id} className="border-t border-stone-100 hover:bg-stone-50">
                                            <td className="py-3 px-4">
                                                <Link to={`/projects/${p.project_id}`} className="text-stone-900 hover:text-amber-700 font-medium">{p.project_name}</Link>
                                            </td>
                                            <td className="py-3 px-4 text-right text-stone-700">{p.vendors_quoted}</td>
                                            <td className="py-3 px-4 text-stone-700">
                                                {p.cheapest_vendor ? <span className="inline-flex items-center gap-1 text-amber-700"><Trophy size={13} /> {p.cheapest_vendor}</span> : "—"}
                                            </td>
                                            <td className="py-3 px-4 text-right font-medium">{inr(p.cheapest_total)}</td>
                                            <td className="py-3 px-4 text-right text-green-700 font-medium">{inr(p.saving)}</td>
                                            <td className="py-3 px-4 text-right">
                                                {p.mismatch_count > 0 ? (
                                                    <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 rounded-md px-1.5 py-0.5 text-xs">
                                                        <TriangleAlert size={12} /> {p.mismatch_count}
                                                    </span>
                                                ) : <span className="text-stone-400">0</span>}
                                            </td>
                                            <td className="py-3 px-4">
                                                {p.budget ? (
                                                    <span className={"inline-block px-2 py-0.5 rounded-md text-xs border " + (p.budget_exceeded ? "bg-red-50 border-red-200 text-red-700" : "bg-green-50 border-green-200 text-green-700")}>
                                                        {p.budget_exceeded ? "Exceeded" : "Within"}
                                                    </span>
                                                ) : <span className="text-stone-400 text-xs">—</span>}
                                            </td>
                                            <td className="py-3 px-4 text-xs text-stone-600 capitalize">{p.order_status?.replace("_", " ")}</td>
                                        </tr>
                                    ))}
                                    {(data.projects || []).length === 0 && (
                                        <tr><td colSpan={8} className="py-10 text-center text-stone-500 text-sm">No projects yet. {isAdmin() && <Link to="/projects" className="text-amber-700">Create the first one →</Link>}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {canSeeMaster && assignments && (
                <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-stone-200 flex items-center gap-2">
                        <UsersIcon size={16} className="text-amber-600" />
                        <div>
                            <div className="font-heading font-semibold text-stone-900">Assignments overview</div>
                            <div className="text-xs text-stone-500">Which purchase member is working on which project and vendor. Admin-only oversight — no prices exposed to other members.</div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                    <th className="text-left py-3 px-4">Project</th>
                                    <th className="text-left py-3 px-4">Purchase member</th>
                                    <th className="text-left py-3 px-4">Department</th>
                                    <th className="text-left py-3 px-4">Assigned vendor</th>
                                    <th className="text-left py-3 px-4">Latest version</th>
                                    <th className="text-left py-3 px-4">Status</th>
                                    <th className="text-left py-3 px-4">Submitted</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(assignments.rows || []).map((r, i) => (
                                    <tr key={i} className="border-t border-stone-100 hover:bg-stone-50">
                                        <td className="py-3 px-4"><Link to={`/projects/${r.project_id}`} className="text-stone-900 hover:text-amber-700 font-medium">{r.project_name}</Link></td>
                                        <td className="py-3 px-4">{r.user_name}</td>
                                        <td className="py-3 px-4 text-stone-600 text-xs">{r.department}</td>
                                        <td className="py-3 px-4 text-stone-700">{r.vendor_name}</td>
                                        <td className="py-3 px-4">
                                            {r.latest_version ? <span className="text-xs font-mono px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800">v{r.latest_version}</span> : <span className="text-stone-400 text-xs">—</span>}
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className={"text-xs px-2 py-0.5 rounded-md border capitalize " + (
                                                r.quote_status === "submitted" ? "bg-green-50 border-green-200 text-green-700" :
                                                r.quote_status === "no_assignment" ? "bg-stone-100 border-stone-200 text-stone-500" :
                                                "bg-amber-50 border-amber-200 text-amber-800")}>
                                                {r.quote_status?.replace("_", " ")}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-xs text-stone-500 font-mono">{r.submitted_at ? r.submitted_at.slice(0, 19).replace("T", " ") : "—"}</td>
                                    </tr>
                                ))}
                                {(assignments.rows || []).length === 0 && <tr><td colSpan={7} className="py-10 text-center text-stone-500 text-sm">No project assignments yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {my && (
                <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-stone-200">
                        <div className="font-heading font-semibold text-stone-900">My projects</div>
                        <div className="text-xs text-stone-500">Only projects you are assigned to. You can see only your own vendor&apos;s quote here.</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                    <th className="text-left py-3 px-4">Project</th>
                                    <th className="text-left py-3 px-4">My vendor(s)</th>
                                    <th className="text-right py-3 px-4">My quotes</th>
                                    <th className="text-right py-3 px-4">My total</th>
                                    <th className="text-left py-3 px-4">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(my.projects || []).map((p) => (
                                    <tr key={p.project_id} className="border-t border-stone-100 hover:bg-stone-50">
                                        <td className="py-3 px-4"><Link to={`/projects/${p.project_id}`} className="text-stone-900 hover:text-amber-700 font-medium">{p.project_name}</Link></td>
                                        <td className="py-3 px-4 text-stone-700">{p.assigned_vendors.map(v => v.name).join(", ") || "—"}</td>
                                        <td className="py-3 px-4 text-right">{p.my_quotations}</td>
                                        <td className="py-3 px-4 text-right">{inr(p.my_total)}</td>
                                        <td className="py-3 px-4">
                                            <span className={"inline-block px-2 py-0.5 rounded-md text-xs border " + (p.submitted ? "bg-green-50 border-green-200 text-green-700" : "bg-amber-50 border-amber-200 text-amber-700")}>
                                                {p.submitted ? "Submitted" : "Pending"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {(my.projects || []).length === 0 && (
                                    <tr><td colSpan={5} className="py-10 text-center text-stone-500 text-sm">No projects assigned to you yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
